import { Component } from 'react';
import { H2, Tab, Tabs } from '@blueprintjs/core';

import { type Scopes, type User } from '../../auth';
import { type Settings } from '../../settings';
import {
  DataList,
  DataListItem,
  DefaultCallout,
  ModuleContent,
  type RequestError,
} from '../../shared';
import { Box, Flex } from '../../shared/components';
import AccountOwnerPanelContainer from '../containers/AccountOwnerPanelContainer';
import { GeneralAcctInfoPanelContainer } from '../containers/GeneralAcctInfoPanelContainer';
import MembersPanelContainer from '../containers/MembersPanelContainer';
import { PowerUsersPanelContainer } from '../containers/PowerUsersPanelContainer';
import {
  type Account,
  type AuditLogEntries,
  type AuditLogEntryDetail,
  type CustomRole,
  type IEapSegment,
  type LegacySubscription,
  type Member,
  type MemberSessions,
  type Plan,
  PlanType,
  type Subscription,
  type SubscriptionUsage,
} from '../model';

import { AccountHeading } from './AccountHeading';
import { AccountSecurityPanel } from './AccountSecurityPanel';
import { AuditLog } from './AuditLog/AuditLog';
import { SubscriptionDetails } from './Billing/SubscriptionDetails';
import { CancelPlanButton } from './CancelPlanButton';
import { DiagnosticUsageTab } from './DiagnosticUsageTab';
import { LegacySubscriptionAccountDetails } from './LegacySubscriptionAccountDetails';
import { ProjectsPanel } from './ProjectsPanel';
import { LinkHub } from './LinkHub';
import { PlanForm, type PlanFormProps } from './PlanForm';
import { PlanUsageTab } from './PlanUsageTab';
import { RolesAndTeamsTab } from './RolesAndTeams';
import { SDKUsageTab } from './SDKUsageTab';
import { SubscriptionAccountDetails } from './SubscriptionAccountDetails';

import './AccountList.scss';

type BillingContactEntryProps = {
  label: string;
  value?: string;
};

const BillingContactEntry = (props: BillingContactEntryProps) => (
  <DataListItem>
    <Flex justify="space-between" align="center">
      <Box>{props.label}</Box>
      <Box ml="auto">{props.value || 'N/A'}</Box>
    </Flex>
  </DataListItem>
);

type AccountDetailsProps = Readonly<{
  settings: Settings;
  scopes: Scopes;
  account: Account;
  // Only fetched for LEGACY plans, so it is legitimately absent otherwise. The
  // legacy panel below is rendered only once this is present.
  legacySubscription: LegacySubscription | null;
  subscription: Subscription;
  isUpdatingSubscription: boolean;
  memberSessions: MemberSessions[];
  customRoles: { [accountId: string]: CustomRole };
  onChangeTrialEnd: (date: Date) => void;
  onChangeGraceEnd: (date: Date) => void;
  onChangeLegacyTrialEnd: (date: Date) => void;
  onChangeLegacyGraceEnd: (date: Date) => void;
  onChangeEnterpriseCampaignDates: (date: Date) => void;
  onChangeGuardianCampaignDates: (date: Date) => void;
  onSubscribe: (plan: Plan) => void;
  onCancelSubscription: (account: Account, endImmediately: boolean) => void;
  onSelectEntry: (entryId: string) => void;
  onCloseEntryDetails: () => void;
  selectedEntryId?: string | null;
  selectedEntryUrl?: string | null;
  enableAuditLogEntryLinks?: boolean;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  onBackToNewest?: () => void;
  onSetMfaDisabled: (account: Account, member: Member) => void;
  setSsoDisabled: () => void;
  isDisablingSso: boolean;
  onSearchParamsChange: ({
    query,
    spec,
    member,
    fuzzy,
    date,
    actions,
  }: {
    query?: string;
    spec?: string;
    member?: string;
    fuzzy?: boolean;
    date?: ReadonlyArray<Date | null | number>;
    actions?: readonly string[];
  }) => void;
  subscriptionUsage: SubscriptionUsage | undefined;
  subscriptionUsageLoading: boolean;
  subscriptionUsageError?: RequestError;
  user: User;
  auditLogEntries?: AuditLogEntries;
  auditLogEntriesLoading?: boolean;
  auditLogEntriesError?: RequestError | null;
  entryDetails?: AuditLogEntryDetail | null;
  entryDetailsLoading?: boolean;
  entryDetailsError?: RequestError | null;
  // Current search parameters for form persistence
  currentAuditQuery?: string;
  currentAuditSpec?: string;
  currentAuditMember?: string;
  currentAuditFuzzy?: boolean;
  currentAuditDate?: ReadonlyArray<Date | null>;
  currentAuditActions?: readonly string[];
  auditLogMembers?: Member[];
  auditLogMembersLoading?: boolean;
  enableVitalAuditLogEnhancedSearch?: boolean;
  eapSegments?: {
    eap: IEapSegment[];
  };
  eapSegmentsRequest?: {
    isFetching: boolean;
    error?: Error;
  };
}>;

type AccountDetailsState = {
  stripeSubscriptionId: string;
  selectedTabId: string;
};

export class AccountDetails extends Component<AccountDetailsProps, AccountDetailsState> {
  public state: AccountDetailsState = {
    stripeSubscriptionId: '',
    selectedTabId: 'members',
  };

  private handleTabChange = (selectedTabId: string) => {
    this.setState({ selectedTabId });
    // The page owns the open audit log entry, so it has to hear about every
    // tab change, not only the ones that drive the tab from the URL.
    this.props.onTabChange?.(selectedTabId);
  };

  public render() {
    const {
      settings,
      scopes,
      account,
      legacySubscription,
      subscription,
      isUpdatingSubscription,
      memberSessions,
      customRoles,
      onChangeTrialEnd,
      onChangeGraceEnd,
      onChangeLegacyTrialEnd,
      onChangeLegacyGraceEnd,
      onSubscribe,
      onCancelSubscription,
      onSearchParamsChange,
      onSelectEntry,
      onCloseEntryDetails,
      selectedEntryId,
      selectedEntryUrl,
      enableAuditLogEntryLinks,
      onBackToNewest,
      subscriptionUsage,
      subscriptionUsageLoading,
      subscriptionUsageError,
      user,
      auditLogEntries,
      auditLogEntriesLoading,
      auditLogEntriesError,
      entryDetails,
      entryDetailsLoading,
      entryDetailsError,
      currentAuditQuery,
      currentAuditSpec,
      currentAuditMember,
      currentAuditFuzzy,
      currentAuditDate,
      currentAuditActions,
      auditLogMembers,
      auditLogMembersLoading,
      enableVitalAuditLogEnhancedSearch,
      onSetMfaDisabled,
      setSsoDisabled,
      isDisablingSso,
      onChangeEnterpriseCampaignDates,
      onChangeGuardianCampaignDates,
    } = this.props;

    const isLegacy: boolean = subscription.planType === PlanType.LEGACY;
    return (
      <>
        <AccountHeading account={account} />

        <Flex>
          <Box width={1 / 2} p={2} mb={2}>
            {isLegacy && legacySubscription ? (
              <LegacySubscriptionAccountDetails
                legacySubscription={legacySubscription}
                accountRequireMfa={account.requireMfa}
                onChangeLegacyGraceEnd={onChangeLegacyGraceEnd}
                onChangeLegacyTrialEnd={onChangeLegacyTrialEnd}
                scopes={scopes}
              />
            ) : (
              <SubscriptionAccountDetails
                account={account}
                subscriptionUsage={subscriptionUsage?._usage}
                subscriptionUsageLoading={subscriptionUsageLoading}
                subscriptionUsageError={subscriptionUsageError}
                subscription={subscription}
                accountRequireMfa={account.requireMfa}
                onChangeTrialEnd={onChangeTrialEnd}
                onChangeGraceEnd={onChangeGraceEnd}
                onChangeEnterpriseCampaignDates={onChangeEnterpriseCampaignDates}
                onChangeGuardianCampaignDates={onChangeGuardianCampaignDates}
                settings={settings}
                scopes={scopes}
              />
            )}
          </Box>
          <Box width={1 / 2} p={2} mb={2}>
            <LinkHub user={user} settings={settings} scopes={scopes} subscription={subscription} account={account} />
          </Box>
        </Flex>
        <Tabs
          id={account._id}
          animate={false}
          renderActiveTabPanelOnly
          selectedTabId={this.props.activeTab ?? this.state.selectedTabId}
          onChange={this.handleTabChange}
        >
          <Tab
            id="members"
            title="Members"
            panel={
              <MembersPanelContainer
                account={account}
                settings={settings}
                scopes={scopes}
                memberSessions={memberSessions}
                customRoles={customRoles}
                onSetMfaDisabled={onSetMfaDisabled}
                marketplaceProvider={
                  subscription.subscriptionType === 'Marketplace' ? subscription.marketplaceProvider : undefined
                }
              />
            }
          />
          {scopes.accountDetail && settings.enableRolesAndTeamsTab && (
            <Tab
              id="rolesAndTeams"
              title="Roles & Teams"
              panel={<RolesAndTeamsTab account={account} customRoles={customRoles} />}
            />
          )}
          {scopes.accountOwnership && (
            <Tab id="owner" title="Owner" panel={<AccountOwnerPanelContainer account={account} />} />
          )}
          {scopes.auditLog && (
            <Tab
              id="auditLogEntries"
              title="Audit log"
              panel={this.getAuditLogPanel(
                account,
                onSelectEntry,
                onSearchParamsChange,
                auditLogEntries,
                auditLogEntriesLoading,
                auditLogEntriesError,
                entryDetails,
                entryDetailsLoading,
                entryDetailsError,
                currentAuditQuery,
                currentAuditSpec,
                currentAuditMember,
                currentAuditFuzzy,
                currentAuditDate,
                currentAuditActions,
                auditLogMembers,
                auditLogMembersLoading,
                enableVitalAuditLogEnhancedSearch,
                selectedEntryId,
                selectedEntryUrl,
                onCloseEntryDetails,
                enableAuditLogEntryLinks,
                onBackToNewest,
              )}
            />
          )}
          {settings.enableGeneralTab && (
            <Tab id="generalAccountInfo" title="General" panel={<GeneralAcctInfoPanelContainer account={account} />} />
          )}
          {settings.enablePowerUsersTab && (
            <Tab id="powerUsers" title="Power Users" panel={<PowerUsersPanelContainer account={account} />} />
          )}
          <Tab
            id="billing"
            title="Billing"
            panel={this.getBillingPanel(
              {
                subscription,
                settings,
                scopes,
                isSubmitting: isUpdatingSubscription,
                onSubmit: onSubscribe,
              },
              onCancelSubscription,
              account,
            )}
          />
          {scopes.accountUsage && settings.enableUsageChartsV2 && (
            <Tab id="planUsage" title="Plan usage" panel={<PlanUsageTab account={account} />} />
          )}
          {scopes.accountUsage && settings.enableUsageChartsV2 && (
            <Tab id="diagnosticUsage" title="Diagnostic usage" panel={<DiagnosticUsageTab account={account} />} />
          )}
          {scopes.accountUsage && settings.enableSdkVersionUsageDetails && (
            <Tab id="sdks" title="SDKs" panel={<SDKUsageTab account={account} />} />
          )}
          <Tab id="project" title="Projects & environments" panel={<ProjectsPanel key={account._id} account={account} />} />
          {scopes.securityTab && (
            <Tab
              id="security"
              title="Security"
              panel={
                <AccountSecurityPanel
                  account={account}
                  subscription={subscription}
                  scopes={scopes}
                  setSsoDisabled={setSsoDisabled}
                  isDisablingSso={isDisablingSso}
                />
              }
            />
          )}
        </Tabs>
      </>
    );
  }

  private getBillingPanel = (
    props: PlanFormProps,
    onCancelSubscription: (account: Account, endImmediately: boolean) => void,
    account: Account,
  ) => (
    <Flex>
      <Box width={1}>
        <H2>Billing contact</H2>
        <Flex>
          <Box width={1 / 2}>
            <ModuleContent snug>
              <DataList>
                <BillingContactEntry label="Name" value={account.billingContact?.name} />
                <BillingContactEntry label="Email" value={account.billingContact?.email} />
                <BillingContactEntry label="Company" value={account.billingContact?.company} />
                <BillingContactEntry label="Address 1" value={account.billingContact?.address1} />
                <BillingContactEntry label="Address 2" value={account.billingContact?.address2} />
              </DataList>
            </ModuleContent>
          </Box>
          <Box width="10px" />
          <Box width={1 / 2}>
            <ModuleContent snug>
              <DataList>
                <BillingContactEntry label="City" value={account.billingContact?.city} />
                <BillingContactEntry label="State" value={account.billingContact?.state} />
                <BillingContactEntry label="Postal Code" value={account.billingContact?.postalCode} />
                <BillingContactEntry label="Country" value={account.billingContact?.country} />
                <BillingContactEntry label="PO Number" value={account.billingContact?.poNumber} />
              </DataList>
            </ModuleContent>
          </Box>
        </Flex>

        <Flex className="AccountDetails-section-spacing">
          <Box width={1 / 2}>
            <>
              <Flex>
                <Box>
                  <H2>Billing plan</H2>
                </Box>
              </Flex>
              {this.props.scopes.enterprisePlanCreator || this.props.scopes.switchTrialType ? (
                <PlanForm {...props} />
              ) : (
                <DefaultCallout>
                  You do not have access to the plan manager. If you believe this is in error, please request access in
                  the{' '}
                  <a href="https://launchdarkly.slack.com/app_redirect?channel=tools-vitalstatistix">
                    #tools-vitalstatistix
                  </a>{' '}
                  Slack channel.
                </DefaultCallout>
              )}
              {this.props.scopes.enterprisePlanCreator && (
                <CancelPlanButton
                  onCancelSubscription={(cancelAccount: Account, endImmediately: boolean) =>
                    onCancelSubscription(cancelAccount, endImmediately)
                  }
                  account={account}
                  cancelImmediatelyIsEnabled={this.props.settings.enableCancelImmediatelyFunctionality}
                />
              )}
            </>
          </Box>
        </Flex>

        <Flex className="AccountDetails-section-spacing">
          <Box width={1 / 2}>
            {this.props.scopes.billingDebug && (
              <SubscriptionDetails account={this.props.account} subscription={this.props.subscription} />
            )}
          </Box>
        </Flex>
      </Box>
    </Flex>
  );

  private getAuditLogPanel = (
    account: Account,
    onSelectEntry: (entryId: string) => void,
    onSearchParamsChange: ({
      query,
      spec,
      member,
      fuzzy,
      date,
      actions,
    }: {
      query?: string;
      spec?: string;
      member?: string;
      fuzzy?: boolean;
      date?: ReadonlyArray<Date | null | number>;
      actions?: readonly string[];
    }) => void,
    auditLogEntries?: AuditLogEntries,
    auditLogEntriesLoading?: boolean,
    auditLogEntriesError?: RequestError | null,
    entryDetails?: AuditLogEntryDetail | null,
    _entryDetailsLoading?: boolean,
    entryDetailsError?: RequestError | null,
    currentAuditQuery?: string,
    currentAuditSpec?: string,
    currentAuditMember?: string,
    currentAuditFuzzy?: boolean,
    currentAuditDate?: ReadonlyArray<Date | null>,
    currentAuditActions?: readonly string[],
    auditLogMembers?: Member[],
    auditLogMembersLoading?: boolean,
    enableVitalAuditLogEnhancedSearch?: boolean,
    selectedEntryId?: string | null,
    selectedEntryUrl?: string | null,
    onCloseEntryDetails?: () => void,
    enableAuditLogEntryLinks?: boolean,
    onBackToNewest?: () => void,
  ) => {
    if (auditLogEntriesLoading) {
      return <div className="AuditLog">Loading audit log entries...</div>;
    }

    if (auditLogEntriesError) {
      const getErrorMessage = (error: RequestError): string => {
        if (typeof error.message === 'string') {
          return error.message;
        }
        if (typeof error.message === 'object' && error.message && 'message' in error.message) {
          return String((error.message as { message: unknown }).message);
        }
        return 'Unknown error';
      };

      return <div className="AuditLog">Error loading audit log entries: {getErrorMessage(auditLogEntriesError)}</div>;
    }

    if (!auditLogEntries) {
      return (
        <div className="AuditLog">
          <DefaultCallout>There are no audit log entries.</DefaultCallout>
        </div>
      );
    }

    return (
      <div className="AuditLog">
        <AuditLog
          account={account}
          auditLogEntries={auditLogEntries}
          entryDetails={entryDetails}
          entryDetailsError={entryDetailsError}
          currentQuery={currentAuditQuery}
          currentSpec={currentAuditSpec}
          currentMember={currentAuditMember}
          currentFuzzy={currentAuditFuzzy}
          currentDate={currentAuditDate}
          currentActions={currentAuditActions}
          members={auditLogMembers}
          membersLoading={auditLogMembersLoading}
          enableVitalAuditLogEnhancedSearch={enableVitalAuditLogEnhancedSearch}
          selectedEntryId={selectedEntryId}
          selectedEntryUrl={selectedEntryUrl}
          enableAuditLogEntryLinks={enableAuditLogEntryLinks}
          onSearchParamsChange={onSearchParamsChange}
          onSelectEntry={onSelectEntry}
          onCloseEntryDetails={onCloseEntryDetails ?? (() => undefined)}
          onBackToNewest={onBackToNewest}
        />
      </div>
    );
  };
}
